import type { TFunction } from "i18next";
import type { SubmitOrderVerificationMutation } from "~/gql/mutations/verify.generated";
import type { MutationFireOutcome } from "~/helpers/offline/classifyMutationFireOutcome";
import type { MutationRawResult } from "~/helpers/runMutation";

import { extractTopLevelGraphqlMessage } from "~/helpers/rtkErrorClassification";
import { stripInternalIdentifiers } from "~/helpers/surfaceMutationError";

export const VERIFY_REFUSAL_CODE = {
	CONCURRENT_WRITE_CONFLICT: "CONCURRENT_WRITE_CONFLICT",
	CONTAINER_ALREADY_VERIFIED: "VERIFY_CONTAINER_ALREADY_VERIFIED",
	CONTAINER_REMOVED: "VERIFY_CONTAINER_REMOVED",
	ORDER_ALREADY_VERIFIED: "ORDER_ALREADY_VERIFIED",
	ORDER_NOT_PICKED: "ORDER_NOT_PICKED",
	ORDER_ON_HOLD: "ORDER_ON_HOLD",
	SHIP_UNCONFIRMED: "SHIP_UNCONFIRMED"
} as const;

const FINAL_REFUSAL_COPY: Record<string, string> = {
	[VERIFY_REFUSAL_CODE.CONTAINER_REMOVED]: "verify.refusals.containerRemoved",
	[VERIFY_REFUSAL_CODE.ORDER_ALREADY_VERIFIED]: "verify.refusals.orderAlreadyVerified",
	[VERIFY_REFUSAL_CODE.ORDER_NOT_PICKED]: "verify.refusals.orderNotPicked",
	[VERIFY_REFUSAL_CODE.SHIP_UNCONFIRMED]: "verify.refusals.shipUnconfirmed"
};

const RETRYABLE_REFUSAL_COPY: Record<string, string> = {
	[VERIFY_REFUSAL_CODE.CONCURRENT_WRITE_CONFLICT]: "verify.refusals.orderChanged",
	[VERIFY_REFUSAL_CODE.CONTAINER_ALREADY_VERIFIED]: "verify.refusals.containerAlreadyVerified",
	[VERIFY_REFUSAL_CODE.ORDER_ON_HOLD]: "verify.refusals.orderOnHold"
};

export interface VerifyRefusalInput {
	code?: string | null;
	message: string;
}

export interface VerifyRefusal {
	isFinal: boolean;
	message: string;
}

export type SubmitOrderVerificationPayload = SubmitOrderVerificationMutation["submitOrderVerification"];

type SubmitOrderVerificationFireOutcome = MutationFireOutcome<SubmitOrderVerificationPayload>;

export type SubmitOrderVerificationOutcome =
	| Extract<SubmitOrderVerificationFireOutcome, { outcome: "success" }>
	| (Extract<SubmitOrderVerificationFireOutcome, { outcome: "failed" }> & { errorCode?: string | null });

export const isFinalVerifyRefusalCode = (code: string | null | undefined): boolean =>
	Boolean(code && FINAL_REFUSAL_COPY[code]);

export const toSubmitOrderVerificationOutcome = (
	fireOutcome: SubmitOrderVerificationFireOutcome,
	payload: null | SubmitOrderVerificationPayload | undefined,
	shipUnconfirmedMessage: string
): SubmitOrderVerificationOutcome => {
	if (fireOutcome.outcome === "success") return fireOutcome;
	if (fireOutcome.outcome === "failed") return { ...fireOutcome, errorCode: payload?.errors?.[0]?.code };
	return {
		errorCode: VERIFY_REFUSAL_CODE.SHIP_UNCONFIRMED,
		errorMessage: shipUnconfirmedMessage,
		outcome: "failed",
		sessionRenewed: false
	};
};

export const describeOrderVerificationFailure = (
	result: MutationRawResult<unknown>,
	fallbackMessage: string
): string => {
	const serverMessage = result.payloadErrors?.[0]?.message || extractTopLevelGraphqlMessage(result.transportError);
	return stripInternalIdentifiers(serverMessage ?? "") || fallbackMessage;
};

const describeVerifyRefusal = (t: TFunction, refusal: VerifyRefusalInput, orderNumber: string): VerifyRefusal => {
	const finalKey = refusal.code ? FINAL_REFUSAL_COPY[refusal.code] : undefined;
	if (finalKey) return { isFinal: true, message: t(finalKey, { order: orderNumber }) };
	const retryableKey = refusal.code ? RETRYABLE_REFUSAL_COPY[refusal.code] : undefined;
	return { isFinal: false, message: retryableKey ? t(retryableKey, { order: orderNumber }) : refusal.message };
};

export default describeVerifyRefusal;
